package com.tovtalk.ui.screen.profile

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.text.font.FontWeight
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

@Composable
fun ProfileUserListScreen(
    userId: Int,
    listType: String,
    onNavigateBack: () -> Unit,
    onNavigateToProfile: (userId: Int, fromPostId: Int?) -> Unit,
    userRepository: UserRepository = koinInject()
) {
    val isFollowing = listType == "following"
    var users by remember { mutableStateOf<List<User>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var currentPage by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    var followLoadingId by remember { mutableStateOf<Int?>(null) }

    val scope = rememberCoroutineScope()
    val listState = rememberLazyListState()

    fun loadPage(page: Int) {
        scope.launch {
            val result = if (isFollowing) {
                userRepository.getFollowing(userId, page)
            } else {
                userRepository.getFollowers(userId, page)
            }
            when (result) {
                is DomainResult.Success -> {
                    if (page == 0) users = result.data else users = users + result.data
                    hasMore = result.data.size >= 20
                    currentPage = page
                }
                else -> hasMore = false
            }
        }
    }

    LaunchedEffect(userId, listType) {
        loading = true
        users = emptyList()
        hasMore = true
        currentPage = 0
        val result = if (isFollowing) {
            userRepository.getFollowing(userId, 0)
        } else {
            userRepository.getFollowers(userId, 0)
        }
        when (result) {
            is DomainResult.Success -> {
                users = result.data
                hasMore = result.data.size >= 20
            }
            else -> {}
        }
        loading = false
    }

    val shouldLoadMore by remember {
        derivedStateOf {
            if (users.isEmpty() || !hasMore || loadingMore) return@derivedStateOf false
            val lastVisibleIndex = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
            lastVisibleIndex >= users.size - 2
        }
    }
    LaunchedEffect(shouldLoadMore) {
        if (shouldLoadMore) {
            loadingMore = true
            val result = if (isFollowing) {
                userRepository.getFollowing(userId, currentPage + 1)
            } else {
                userRepository.getFollowers(userId, currentPage + 1)
            }
            when (result) {
                is DomainResult.Success -> {
                    users = users + result.data
                    hasMore = result.data.size >= 20
                    currentPage = currentPage + 1
                }
                else -> hasMore = false
            }
            loadingMore = false
        }
    }

    fun toggleFollow(u: User) {
        scope.launch {
            followLoadingId = u.id
            if (u.isFollowing) {
                when (userRepository.unfollowUser(u.id)) {
                    is DomainResult.Success -> users = users.map { if (it.id == u.id) it.copy(isFollowing = false) else it }
                    else -> {}
                }
            } else {
                when (userRepository.followUser(u.id)) {
                    is DomainResult.Success -> users = users.map { if (it.id == u.id) it.copy(isFollowing = true) else it }
                    else -> {}
                }
            }
            followLoadingId = null
        }
    }

    val title = if (isFollowing) {
        StringProvider.get("profile_following")
    } else {
        StringProvider.get("profile_followers")
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = title,
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        if (loading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = TovTalkTheme.colors.brand)
            }
        } else if (users.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = if (isFollowing) StringProvider.get("settings_my_following_empty") else StringProvider.get("settings_my_following_empty"),
                    style = TovTalkTheme.typography.bodyLarge,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xs)
            ) {
                items(users, key = { it.id }) { user ->
                    ProfileUserListItem(
                        user = user,
                        onProfileClick = { onNavigateToProfile(user.id, null) },
                        onFollowClick = { toggleFollow(user) },
                        isFollowLoading = followLoadingId == user.id
                    )
                }
                if (loadingMore) {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(TovTalkTheme.spacing.md),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(24.dp),
                                color = TovTalkTheme.colors.brand
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProfileUserListItem(
    user: User,
    onProfileClick: () -> Unit,
    onFollowClick: () -> Unit,
    isFollowLoading: Boolean
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onProfileClick)
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = user.profilePhoto,
            name = user.fullname,
            size = 48.dp
        )
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(horizontal = TovTalkTheme.spacing.md)
        ) {
            Text(
                text = user.fullname.ifBlank { user.username },
                style = TovTalkTheme.typography.bodyLarge,
                fontWeight = FontWeight.Medium,
                color = TovTalkTheme.colors.textPrimary
            )
            Text(
                text = "@${user.username}",
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary
            )
        }
        TextButton(
            onClick = onFollowClick,
            enabled = !isFollowLoading
        ) {
            if (isFollowLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(20.dp),
                    color = TovTalkTheme.colors.brand,
                    strokeWidth = 2.dp
                )
            } else {
                Text(
                    text = if (user.isFollowing) StringProvider.get("profile_unfollow") else StringProvider.get("profile_follow"),
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        }
    }
}
